/**
 * Runtime (zod) schema for a template manifest crossing a trust boundary —
 * stored jsonb rows, request bodies, agent-authored files. `parseTemplateManifest`
 * is the one validator every read path shares; the structural rules beyond
 * shape (slot ⇄ markup bijection, binding grammar, target fit) live in
 * validate.ts.
 */

import { z } from "zod";
import { AdCommitValidationError } from "../../llm/agents/insights/ads/errors";
import type { TemplateManifest } from "./types";

const feedSourceSchema = z.object({
  from: z.literal("feed"),
  binding: z.string(),
});

const slotSourceSchema = z.union([
  feedSourceSchema,
  z.object({
    from: z.literal("agent"),
    instruction: z.string(),
    produces: z
      .union([
        z.object({
          kind: z.enum(["text", "image"]),
          provenance: z.enum(["generated", "any"]).optional(),
        }),
        z.object({
          kind: z.literal("image"),
          provenance: z.literal("catalog"),
          assetGroupId: z.string().trim().min(1),
        }),
      ])
      .optional(),
    references: z.array(feedSourceSchema).optional(),
  }),
]);

const slotPlaceholderSchema = z.union([
  z.object({ kind: z.literal("text"), text: z.string() }),
  z.object({
    kind: z.literal("image"),
    width: z.number().positive(),
    height: z.number().positive(),
  }),
]);

const slotTransformSchema = z.object({
  kind: z.literal("cutout"),
  shadow: z.object({ strength: z.number() }).optional(),
});

const slotTargetSchema = z.union([
  z.object({ kind: z.literal("content") }),
  z.object({ kind: z.literal("attr"), name: z.literal("src") }),
]);

/** Structural schema for an over-the-wire manifest. */
export const templateManifestSchema = z.object({
  canvas: z
    .object({ width: z.number().positive(), height: z.number().positive() })
    .optional(),
  slots: z
    .array(
      z.object({
        id: z.string().min(1),
        optional: z.boolean().optional(),
        target: slotTargetSchema.optional(),
        source: slotSourceSchema,
        placeholder: slotPlaceholderSchema.optional(),
        transform: z.array(slotTransformSchema).optional(),
      }),
    )
    .min(1),
});

export function parseTemplateManifest(input: unknown): TemplateManifest {
  const parsed = templateManifestSchema.safeParse(input);
  if (!parsed.success) {
    throw new AdCommitValidationError(
      `Invalid template manifest: ${parsed.error.message}`,
    );
  }
  // The cast restores the required props that zod inference loses under the
  // backend tsconfig's strict:false (same pattern as commit-ad's AdDiff).
  return parsed.data as TemplateManifest;
}
